import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Search, Filter, Droplets, ShoppingCart, 
  Plus, ChevronRight, X
} from 'lucide-react';

const CatalogPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { addToCart, cartCount } = useCart();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null);

  const categories = [
    { id: 'super', name: 'Super', color: 'bg-red-500' },
    { id: 'extra', name: 'Extra', color: 'bg-orange-500' },
    { id: 'classic', name: 'Classic', color: 'bg-amber-500' },
    { id: 'diesel_turbo', name: 'Diesel Turbo', color: 'bg-zinc-700' },
    { id: 'moto', name: 'Moto', color: 'bg-blue-500' },
  ];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await api().get('/products');
        setProducts(res.data);
        setFilteredProducts(res.data);
      } catch (err) {
        console.error('Error loading products:', err);
      }
      setLoading(false);
    };
    loadProducts();
  }, [api]);

  useEffect(() => {
    let filtered = products;
    
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        p.viscosity.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredProducts(filtered);
  }, [products, selectedCategory, search]);

  const handleAddToCart = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setAddingToCart(productId);
    try {
      await addToCart(productId, 1);
      toast.success('Produto adicionado ao cesto!');
    } catch (err) {
      toast.error('Erro ao adicionar ao cesto');
    }
    setAddingToCart(null);
  };

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-barlow font-bold text-xl flex-1">Catálogo</h1>
          <Link 
            to="/cart" 
            className="relative w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center"
            data-testid="cart-header-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-sonangol-red text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar produtos..."
            className="pl-10 h-11 bg-zinc-50"
            data-testid="search-input"
          />
          {search && (
            <button 
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 py-3 overflow-x-auto">
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory 
                ? 'bg-sonangol-red text-white' 
                : 'bg-white text-zinc-600 border border-zinc-200'
            }`}
            data-testid="category-all"
          >
            Todos
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.id 
                  ? 'bg-sonangol-red text-white' 
                  : 'bg-white text-zinc-600 border border-zinc-200'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products List */}
      <div className="px-4 space-y-3">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl h-28 animate-pulse"></div>
          ))
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Droplets className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 font-manrope">Nenhum produto encontrado</p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <Link 
              key={product.id} 
              to={`/product/${product.id}`}
              data-testid={`product-item-${product.id}`}
            >
              <div className="bg-white rounded-xl p-4 flex gap-4 items-start active-scale shadow-sm border border-zinc-100">
                <div className="w-20 h-20 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <Droplets className={`w-10 h-10 text-sonangol-red ${product.image_url ? 'hidden' : ''}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-barlow font-bold text-zinc-800">{product.name}</h3>
                      <p className="text-xs text-zinc-500 font-manrope mt-0.5">
                        {product.viscosity} · {product.base_type === 'synthetic' ? 'Sintético' : 
                          product.base_type === 'semi_synthetic' ? 'Semi-Sintético' : 'Mineral'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {product.volume_liters}L
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="font-barlow font-bold text-lg text-sonangol-red">
                      {product.price_aoa.toLocaleString('pt-AO')} <span className="text-sm">AOA</span>
                    </p>
                    <Button
                      size="sm"
                      className="h-9 bg-sonangol-red hover:bg-red-700"
                      onClick={(e) => handleAddToCart(product.id, e)}
                      disabled={addingToCart === product.id}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default CatalogPage;
